import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, PieChart, Pie, Cell, Legend, ReferenceLine } from "recharts";
import { Download, FilePdf, EnvelopeSimple, PaperPlaneRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useChartColors } from "@/lib/useChartColors";

const COLORS = ["hsl(48 100% 51%)", "#38bdf8", "#a855f7", "#22c55e", "#ef4444", "#f59e0b", "#a1a1aa"];
const WEEKDAYS = [["Monday",0],["Tuesday",1],["Wednesday",2],["Thursday",3],["Friday",4],["Saturday",5],["Sunday",6]];

export default function Reports() {
  const { user } = useAuth();
  const chart = useChartColors();
  const [data, setData] = useState(null);
  const [fairness, setFairness] = useState(null);
  const [sat, setSat] = useState(null);
  const [itil, setItil] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [sending, setSending] = useState(false);
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useState({ from: params.get("rfrom") || "", to: params.get("rto") || "" });
  const [scope, setScope] = useState("all");  // admin-only: 'all' | 'team'
  const [ticketType, setTicketType] = useState(params.get("ttype") || "all");
  const TICKET_TYPES = ["Incident", "Service Request", "Proactive Tasks", "Security Incident"];
  const typeLabel = ticketType === "all" ? "Tickets" : ticketType === "Proactive Tasks" ? "Proactive Tasks" : `${ticketType}s`;

  const loadMetrics = (r = range, sc = scope, tt = ticketType) => {
    const params = {};
    if (r.from) params.date_from = r.from;
    if (r.to) params.date_to = r.to;
    const sumParams = { ...params, ...(sc === "team" ? { scope: "team" } : {}), ...(tt && tt !== "all" ? { ticket_type: tt } : {}) };
    api.get("/reports/summary", { params: sumParams }).then(res => setData(res.data));
    api.get("/reports/itil", { params }).then(res => setItil(res.data)).catch(() => {});
  };
  useEffect(() => {
    loadMetrics();
    api.get("/reports/fairness").then(r => setFairness(r.data)).catch(() => {});
    api.get("/reports/satisfaction").then(r => setSat(r.data)).catch(() => {});
    api.get("/reports/schedule").then(r => setSchedule({ ...r.data, recipientsText: (r.data.recipients || []).join(", ") })).catch(() => {});
    // eslint-disable-next-line
  }, []);
  const applyRange = (r) => {
    setRange(r);
    loadMetrics(r, scope);
    const p = new URLSearchParams(params);
    if (r.from) p.set("rfrom", r.from); else p.delete("rfrom");
    if (r.to) p.set("rto", r.to); else p.delete("rto");
    setParams(p, { replace: true });
  };
  const applyScope = (s) => { setScope(s); loadMetrics(range, s); };
  const applyType = (t) => {
    setTicketType(t); loadMetrics(range, scope, t);
    const p = new URLSearchParams(params);
    if (t && t !== "all") p.set("ttype", t); else p.delete("ttype");
    setParams(p, { replace: true });
  };
  const fmtD = (d) => d.toISOString().slice(0, 10);
  const setPreset = (kind) => {
    const to = new Date();
    let from = new Date();
    if (kind === "7") from.setDate(to.getDate() - 6);
    else if (kind === "30") from.setDate(to.getDate() - 29);
    else if (kind === "90") from.setDate(to.getDate() - 89);
    else if (kind === "month") from = new Date(to.getFullYear(), to.getMonth(), 1);
    applyRange({ from: fmtD(from), to: fmtD(to) });
  };
  const PRESETS = [["7", "7D"], ["30", "30D"], ["90", "90D"], ["month", "This month"]];
  if (!data) return <div className="p-6 text-muted-foreground">Loading reports…</div>;

  const isAdmin = user?.role === "admin";
  const parseRecipients = () => (schedule.recipientsText || "").split(",").map(s => s.trim()).filter(Boolean);

  const saveSchedule = async () => {
    try {
      const payload = { enabled: schedule.enabled, frequency: schedule.frequency, day_of_week: schedule.day_of_week, hour: schedule.hour, recipients: parseRecipients() };
      const { data: d } = await api.put("/reports/schedule", payload);
      setSchedule({ ...d, recipientsText: (d.recipients || []).join(", ") });
      toast.success("Report schedule saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const sendNow = async () => {
    const recips = parseRecipients();
    if (!recips.length) { toast.error("Add at least one recipient first"); return; }
    setSending(true);
    try {
      await api.put("/reports/schedule", { enabled: schedule.enabled, frequency: schedule.frequency, day_of_week: schedule.day_of_week, hour: schedule.hour, recipients: recips });
      const { data: r } = await api.post("/reports/schedule/send-now");
      if (r.status === "sent") toast.success(r.detail);
      else if (r.status === "simulated") toast.info(r.detail);
      else toast.error(r.detail || "Send failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const exportCsv = () => {
    const rows = [
      ["Metric","Value"],
      ...((range.from || range.to) ? [["Reporting period", `${range.from || 'start'} to ${range.to || 'today'}`]] : []),
      ["Ticket type", ticketType === "all" ? "All types" : ticketType],
      [`Total ${typeLabel}`, data.total],
      ["Open", data.open],
      ["Resolved", data.resolved],
      ["Breached", data.breached],
      ["MTTR (hours)", data.mttr_hours],
      ["Achieved Resolution - working (hours)", data.achieved_resolution_hours],
      ["MTTA / Achieved Response (hours)", data.achieved_response_hours ?? data.mtta_hours],
      ["SLA Compliance (%)", data.sla_compliance],
      [""],
      ["By Priority"],
      ...Object.entries(data.by_priority).map(([k,v]) => [k,v]),
      [""],
      ["By Status"],
      ...Object.entries(data.by_status).map(([k,v]) => [k,v]),
      [""],
      ["By Category"],
      ...Object.entries(data.by_category).map(([k,v]) => [k,v]),
      [""],
      ["Agent Performance"],
      ["Agent","Assigned","Resolved"],
      ...data.agent_performance.map(a => [a.name, a.assigned, a.resolved]),
    ];
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `itil-report-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Report downloaded");
  };

  const exportPdf = async () => {
    try {
      const params = {};
      if (range.from) params.date_from = range.from;
      if (range.to) params.date_to = range.to;
      if (scope === "team") params.scope = "team";
      if (ticketType !== "all") params.ticket_type = ticketType;
      const res = await api.get("/reports/summary/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `trak-report-${new Date().toISOString().slice(0,10)}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF report downloaded");
    } catch (e) {
      toast.error("PDF export failed");
    }
  };

  const catData = Object.entries(data.by_category).map(([k,v]) => ({ name: k, value: v }));
  const statusData = Object.entries(data.by_status).map(([k,v]) => ({ name: k.replace('_',' '), value: v }));

  return (
    <div className="p-6 space-y-6" data-testid="reports-view">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">ITIL Reporting</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black">Analytics Suite</h1>
        </div>
        {user?.role === "admin" && (
          <Button data-testid="backfill-client-btn" variant="outline" size="sm"
            className="rounded-sm font-mono text-[10px] uppercase h-10"
            onClick={async () => {
              if (!window.confirm("Set the Client on old tickets that have none, using each ticket's requester email domain? This runs once and is safe to repeat.")) return;
              try {
                const { data } = await api.post("/incidents/backfill-client");
                toast.success(`Backfill done — ${data.updated} ticket(s) updated, ${data.skipped} left blank (no matching domain rule).`);
              } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed"); }
            }}>Backfill client on old tickets</Button>
        )}
        {user?.role === "admin" && (
          <Button data-testid="cleanup-converted-btn" variant="outline" size="sm"
            className="rounded-sm font-mono text-[10px] uppercase h-10 border-rag-red-fg/40 text-rag-red-fg hover:bg-rag-red-bg/40"
            onClick={async () => {
              if (!window.confirm("Permanently delete leftover Incident tickets that were already converted to a Service Request / Proactive Task (matched via the reclassification note)? This cannot be undone.")) return;
              try {
                const { data } = await api.post("/incidents/cleanup-converted");
                if (data.removed) toast.success(`Removed ${data.removed} converted incident(s): ${data.tickets.map(t => t.number).join(", ")}`);
                else toast.info(`No leftover converted incidents found (${data.candidates} conversion note(s) checked).`);
                loadMetrics();
              } catch (e) { toast.error(e?.response?.data?.detail || "Cleanup failed"); }
            }}>Remove converted incidents</Button>
        )}
        <div className="flex items-end gap-2 flex-wrap">
          {user?.role === "admin" && (
            <div className="flex items-center gap-1 border border-border rounded-sm p-0.5 h-10" data-testid="reports-scope-toggle">
              {[["all", "All Members"], ["team", "My Team"]].map(([val, label]) => (
                <button key={val} data-testid={`reports-scope-${val}`} onClick={() => applyScope(val)}
                  className={`px-2.5 h-8 text-[10px] font-mono uppercase tracking-wider rounded-sm transition-colors ${scope === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent/40"}`}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <div className="w-44"><Label className="text-[10px] font-mono uppercase text-muted-foreground">Ticket type</Label>
            <Select value={ticketType} onValueChange={applyType}>
              <SelectTrigger data-testid="reports-type-select" className="rounded-sm h-10 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {TICKET_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1 mr-1" data-testid="reports-presets">
            {PRESETS.map(([k, label]) => (
              <Button key={k} data-testid={`reports-preset-${k}`} onClick={() => setPreset(k)} variant="outline" size="sm" className="rounded-sm font-mono text-[10px] uppercase h-10 px-2.5">{label}</Button>
            ))}
          </div>
          <div className="w-36"><Label className="text-[10px] font-mono uppercase text-muted-foreground">From</Label><DatePicker testid="reports-date-from" value={range.from} onChange={(v) => applyRange({ ...range, from: v })} placeholder="Start" /></div>
          <div className="w-36"><Label className="text-[10px] font-mono uppercase text-muted-foreground">To</Label><DatePicker testid="reports-date-to" value={range.to} onChange={(v) => applyRange({ ...range, to: v })} placeholder="End" /></div>
          {(range.from || range.to) && <Button data-testid="reports-range-clear" onClick={() => applyRange({ from: "", to: "" })} variant="ghost" className="rounded-sm font-mono text-[10px] uppercase h-10">Clear</Button>}
          <Button data-testid="export-pdf-btn" onClick={exportPdf} className="rounded-sm font-mono text-xs uppercase tracking-widest h-10">
            <FilePdf size={14} className="mr-2" />Export PDF
          </Button>
          <Button data-testid="export-csv-btn" onClick={exportCsv} variant="outline" className="rounded-sm font-mono text-xs uppercase tracking-widest h-10">
            <Download size={14} className="mr-2" />Export CSV
          </Button>
        </div>
      </div>
      {(range.from || range.to || ticketType !== "all") && <div className="text-[11px] font-mono text-muted-foreground" data-testid="reports-range-note">Showing {ticketType === "all" ? "all ticket types" : `${typeLabel} only (by current type — reclassified tickets count under their new type)`} {range.from ? `from ${range.from}` : ""} {range.to ? `to ${range.to}` : ""} · fairness &amp; satisfaction remain all-time</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">SLA Compliance</div>
          <div className="font-display text-4xl text-primary">{data.sla_compliance}%</div>
        </Card>
        <Card className="p-6 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">MTTR (hours)</div>
          <div className="font-display text-4xl">{data.mttr_hours}</div>
          <div className="text-[11px] font-mono text-muted-foreground mt-1" data-testid="reports-achieved-mttr">achieved (working): {data.achieved_resolution_hours ?? '—'} h</div>
        </Card>
        <Card className="p-6 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Open Backlog</div>
          <div className="font-display text-4xl text-amber-700 dark:text-amber-400">{data.open}</div>
        </Card>
        <Card className="p-6 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Breached</div>
          <div className={`font-display text-4xl ${data.breached ? 'text-red-700 dark:text-red-400' : ''}`}>{data.breached}</div>
        </Card>
      </div>

      <Card className="p-6 border-border rounded-sm">
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Ticket Volume Trend</div>
        <div className="font-display text-lg mb-3">Last 7 Days</div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data.trend_7d}>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
            <XAxis dataKey="date" stroke={chart.axis} tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
            <YAxis stroke={chart.axis} tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} allowDecimals={false} />
            <Tooltip contentStyle={chart.tooltip} />
            <Line type="monotone" dataKey="count" stroke="hsl(48 100% 51%)" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Category Distribution</div>
          <div className="font-display text-lg mb-3">By Category</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={catData} dataKey="value" nameKey="name" outerRadius={90} label>
                {catData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
              <Tooltip contentStyle={chart.tooltip} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-6 border-border rounded-sm">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Status Mix</div>
          <div className="font-display text-lg mb-3">Lifecycle State</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={statusData}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis dataKey="name" stroke={chart.axis} tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
              <YAxis stroke={chart.axis} tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip contentStyle={chart.tooltip} />
              <Bar dataKey="value" fill="hsl(48 100% 51%)" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-6 border-border rounded-sm">
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Agent Performance</div>
        <div className="font-display text-lg mb-3">Assigned vs Resolved</div>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.agent_performance}>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
            <XAxis dataKey="name" stroke={chart.axis} tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
            <YAxis stroke={chart.axis} tick={{ fontSize: 11 }} allowDecimals={false} />
            <Tooltip contentStyle={chart.tooltip} />
            <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
            <Bar dataKey="assigned" fill="hsl(240 5% 45%)" />
            <Bar dataKey="resolved" fill="hsl(48 100% 51%)" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {itil && (
        <div className="space-y-4" data-testid="itil-section">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">ITIL / ISO 20000</div>
            <h2 className="font-display text-2xl font-black">Service Management Metrics</h2>
            <p className="text-sm text-muted-foreground mt-1">Response SLA, acknowledgement speed, first-contact resolution, reopen and escalation health, plus change &amp; problem outcomes.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-6 border-border rounded-sm" data-testid="itil-response-sla">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Response SLA</div>
              <div className="font-display text-4xl text-primary">{itil.sla_response_compliance}%</div>
            </Card>
            <Card className="p-6 border-border rounded-sm" data-testid="itil-mtta">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">MTTA (hours)</div>
              <div className="font-display text-4xl">{itil.mtta_hours}</div>
            </Card>
            <Card className="p-6 border-border rounded-sm" data-testid="itil-fcr">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">First Contact Resolution</div>
              <div className="font-display text-4xl text-emerald-700 dark:text-emerald-400">{itil.fcr_rate}%</div>
            </Card>
            <Card className="p-6 border-border rounded-sm" data-testid="itil-reopen">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Reopen Rate</div>
              <div className={`font-display text-4xl ${itil.reopen_rate > 10 ? 'text-red-700 dark:text-red-400' : ''}`}>{itil.reopen_rate}%</div>
            </Card>
            <Card className="p-6 border-border rounded-sm" data-testid="itil-escalation">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Escalation Rate</div>
              <div className={`font-display text-4xl ${itil.escalation_rate > 25 ? 'text-amber-700 dark:text-amber-400' : ''}`}>{itil.escalation_rate}%</div>
            </Card>
            <Card className="p-6 border-border rounded-sm" data-testid="itil-change-success">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Change Success Rate</div>
              <div className="font-display text-4xl text-primary">{itil.change_success_rate}%</div>
              <div className="text-[10px] font-mono text-muted-foreground mt-1">{itil.changes_total} changes</div>
            </Card>
            <Card className="p-6 border-border rounded-sm" data-testid="itil-known-errors">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Known Errors</div>
              <div className="font-display text-4xl">{itil.known_errors}<span className="text-xl text-muted-foreground">/{itil.problems_total}</span></div>
            </Card>
            <Card className="p-6 border-border rounded-sm" data-testid="itil-workarounds">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Problems w/ Workaround</div>
              <div className="font-display text-4xl">{itil.problems_with_workaround}</div>
            </Card>
          </div>
          <Card className="p-6 border-border rounded-sm">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Open Backlog Aging</div>
            <div className="font-display text-lg mb-3">Unresolved Tickets by Age</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={itil.backlog_aging}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="bucket" stroke={chart.axis} tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                <YAxis stroke={chart.axis} tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={chart.tooltip} />
                <Bar dataKey="count" fill="hsl(48 100% 51%)" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {fairness && (
        <div className="space-y-4" data-testid="fairness-section">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Auto-Assignment</div>
            <h2 className="font-display text-2xl font-black">Assignment Fairness</h2>
            <p className="text-sm text-muted-foreground mt-1">Open-ticket load distribution across agents. Lower spread (std dev) means a more balanced queue.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-6 border-border rounded-sm" data-testid="fairness-avg">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Avg Open / Agent</div>
              <div className="font-display text-4xl">{fairness.avg_open}</div>
            </Card>
            <Card className="p-6 border-border rounded-sm" data-testid="fairness-stddev">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Std Deviation</div>
              <div className={`font-display text-4xl ${fairness.stddev_open > fairness.avg_open ? 'text-amber-700 dark:text-amber-400' : 'text-primary'}`}>{fairness.stddev_open}</div>
            </Card>
            <Card className="p-6 border-border rounded-sm" data-testid="fairness-spread">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Min / Max Load</div>
              <div className="font-display text-4xl">{fairness.min_open}<span className="text-muted-foreground text-2xl mx-2">/</span>{fairness.max_open}</div>
            </Card>
            <Card className="p-6 border-border rounded-sm" data-testid="fairness-unassigned">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Unassigned Open</div>
              <div className={`font-display text-4xl ${fairness.unassigned_open ? 'text-red-700 dark:text-red-400' : ''}`}>{fairness.unassigned_open}</div>
            </Card>
          </div>
          <Card className="p-6 border-border rounded-sm">
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Per-Agent Load</div>
            <div className="font-display text-lg mb-3">Open Tickets per Agent</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={fairness.agents}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="name" stroke={chart.axis} tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                <YAxis stroke={chart.axis} tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={chart.tooltip} />
                <ReferenceLine y={fairness.avg_open} stroke={chart.axis} strokeDasharray="4 4" label={{ value: `avg ${fairness.avg_open}`, fill: chart.axis, fontSize: 10, position: 'right' }} />
                <Bar dataKey="open" fill="hsl(48 100% 51%)" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}

      {sat && (
        <div className="space-y-4" data-testid="satisfaction-section">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Voice of Customer</div>
            <h2 className="font-display text-2xl font-black">Customer Satisfaction</h2>
            <p className="text-sm text-muted-foreground mt-1">Post-resolution CSAT and relationship NPS, with feedback trends over time.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-6 border-border rounded-sm" data-testid="csat-avg-tile">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">CSAT Score</div>
              <div className="font-display text-4xl text-primary">{sat.csat_avg}<span className="text-xl text-muted-foreground">/5</span></div>
              <div className="text-[10px] font-mono text-muted-foreground mt-1">{sat.csat_responses} responses</div>
            </Card>
            <Card className="p-6 border-border rounded-sm" data-testid="csat-pct-tile">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">% Satisfied (4–5★)</div>
              <div className="font-display text-4xl text-emerald-700 dark:text-emerald-400">{sat.csat_pct}%</div>
            </Card>
            <Card className="p-6 border-border rounded-sm" data-testid="nps-tile">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">NPS</div>
              <div className={`font-display text-4xl ${sat.nps_score >= 0 ? 'text-primary' : 'text-red-700 dark:text-red-400'}`}>{sat.nps_score}</div>
              <div className="text-[10px] font-mono text-muted-foreground mt-1">{sat.nps_responses} responses</div>
            </Card>
            <Card className="p-6 border-border rounded-sm" data-testid="nps-split-tile">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Promoters / Passives / Detractors</div>
              <div className="font-display text-2xl">
                <span className="text-emerald-700 dark:text-emerald-400">{sat.promoters}</span>
                <span className="text-muted-foreground mx-1">/</span>
                <span className="text-amber-700 dark:text-amber-400">{sat.passives}</span>
                <span className="text-muted-foreground mx-1">/</span>
                <span className="text-red-700 dark:text-red-400">{sat.detractors}</span>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-6 border-border rounded-sm">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">CSAT Distribution</div>
              <div className="font-display text-lg mb-3">Ratings 1–5</div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={sat.distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="score" stroke={chart.axis} tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                  <YAxis stroke={chart.axis} tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={chart.tooltip} />
                  <Bar dataKey="count" fill="hsl(48 100% 51%)" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
            <Card className="p-6 border-border rounded-sm">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1">Feedback Trend (30d)</div>
              <div className="font-display text-lg mb-3">CSAT & NPS over time</div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={sat.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="date" stroke={chart.axis} tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} />
                  <YAxis yAxisId="l" domain={[0,5]} stroke={chart.axis} tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="r" orientation="right" domain={[-100,100]} stroke={chart.axis} tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={chart.tooltip} />
                  <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
                  <Line yAxisId="l" type="monotone" dataKey="csat_avg" name="CSAT" stroke="hsl(48 100% 51%)" strokeWidth={2} connectNulls dot={false} />
                  <Line yAxisId="r" type="monotone" dataKey="nps" name="NPS" stroke="#38bdf8" strokeWidth={2} connectNulls dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {sat.recent_comments.length > 0 && (
            <Card className="p-6 border-border rounded-sm" data-testid="csat-comments">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Recent Comments</div>
              <div className="space-y-2">
                {sat.recent_comments.map((c, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm border-b border-border/60 pb-2">
                    <span className="font-mono text-primary text-xs shrink-0">{c.ticket_number}</span>
                    <span className="text-primary shrink-0">{"★".repeat(c.score)}<span className="text-muted-foreground">{"★".repeat(5-c.score)}</span></span>
                    <span className="text-muted-foreground">{c.comment}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {isAdmin && schedule && (
        <Card className="p-6 border-border rounded-sm" data-testid="report-schedule-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <EnvelopeSimple size={16} className="text-primary" />
              <span className="text-[11px] font-mono uppercase tracking-widest text-primary">Scheduled Email Reports</span>
            </div>
            <Switch data-testid="schedule-enabled" checked={schedule.enabled} onCheckedChange={(v)=>setSchedule({...schedule, enabled: v})} />
          </div>
          <p className="text-xs text-muted-foreground mb-4">Automatically email this PDF report to managers. Requires Microsoft 365 (Office 365 / Graph) email delivery to be configured; otherwise "Send now" runs in simulation.</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Frequency</Label>
              <Select value={schedule.frequency} onValueChange={(v)=>setSchedule({...schedule, frequency: v})}>
                <SelectTrigger data-testid="schedule-frequency" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem></SelectContent>
              </Select>
            </div>
            {schedule.frequency === "weekly" && (
              <div>
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">Day</Label>
                <Select value={String(schedule.day_of_week)} onValueChange={(v)=>setSchedule({...schedule, day_of_week: parseInt(v)})}>
                  <SelectTrigger data-testid="schedule-day" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{WEEKDAYS.map(([l,d])=><SelectItem key={d} value={String(d)}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Hour (IST)</Label>
              <Input type="number" min="0" max="23" data-testid="schedule-hour" className="mt-1 rounded-sm font-mono" value={schedule.hour} onChange={(e)=>setSchedule({...schedule, hour: parseInt(e.target.value||0)})} />
            </div>
            <div className={schedule.frequency === "weekly" ? "md:col-span-4" : "md:col-span-2"}>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Recipients (comma-separated)</Label>
              <Input data-testid="schedule-recipients" className="mt-1 rounded-sm font-mono text-xs" placeholder="manager@acme.com, lead@acme.com" value={schedule.recipientsText} onChange={(e)=>setSchedule({...schedule, recipientsText: e.target.value})} />
            </div>
          </div>
          {schedule.last_sent && <div className="text-[10px] font-mono text-muted-foreground mt-3">Last sent: {new Date(schedule.last_sent).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST</div>}
          <div className="flex gap-2 mt-4">
            <Button data-testid="schedule-save" onClick={saveSchedule} className="rounded-sm font-mono uppercase text-xs">Save Schedule</Button>
            <Button data-testid="schedule-send-now" onClick={sendNow} disabled={sending} variant="outline" className="rounded-sm font-mono uppercase text-xs border-primary/40"><PaperPlaneRight size={13} className="mr-2" />{sending ? "Sending…" : "Send now"}</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
